import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Music2, ExternalLink, Ticket } from "lucide-react";
import { api } from "@/lib/api";

const sp = (n) => `https://open.spotify.com/search/${encodeURIComponent(n)}`;
const yt = (n) => `https://www.youtube.com/results?search_query=${encodeURIComponent(n + " zydeco")}`;
const GROUPS = [["headliner", "Headliners"], ["rising", "Rising Acts"], ["festival", "On This Month's Festival Bills"]];

export const ArtistCard = ({ a, i }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
    className={`glass flex flex-col rounded-2xl p-6 ${a.tier === "headliner" ? "ring-gold" : ""}`} data-testid={`artist-card-${a.id}`}>
    <span className="font-mono-label text-[10px] uppercase tracking-widest text-cyan-400">{a.tag}</span>
    <h3 className="mt-2 font-cond text-2xl font-extrabold uppercase leading-tight text-white">{a.name}</h3>
    <p className="mt-3 flex-1 text-sm text-neutral-400">{a.blurb}</p>
    <div className="mt-5 flex gap-2">
      <a href={sp(a.name)} target="_blank" rel="noreferrer" data-testid={`artist-spotify-${a.id}`} className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 font-mono-label text-[10px] uppercase tracking-wider text-neutral-300 hover:border-cyan-500 hover:text-cyan-400"><Music2 className="h-3 w-3" /> Spotify</a>
      <a href={yt(a.name)} target="_blank" rel="noreferrer" data-testid={`artist-youtube-${a.id}`} className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 font-mono-label text-[10px] uppercase tracking-wider text-neutral-300 hover:border-cyan-500 hover:text-cyan-400"><ExternalLink className="h-3 w-3" /> YouTube</a>
    </div>
  </motion.div>
);

export default function ArtistsPage() {
  const [artists, setArtists] = useState([]);
  useEffect(() => { api.get("/artists").then((r) => setArtists(r.data)).catch(() => {}); }, []);
  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20" data-testid="artists-page">
      <span className="eyebrow">Talent Roster · All Genres</span>
      <h1 className="mt-2 font-display text-4xl uppercase text-chrome sm:text-5xl lg:text-6xl">Talent For <span className="text-gold">Every Stage.</span></h1>
      <p className="mt-4 max-w-2xl text-neutral-300">E.O.N. books across every genre — hip-hop, R&B, country, gospel, Latin, EDM, comedy and keynote speakers. Below is the current zydeco & southern-soul roster; request any artist through the Talent Booking lane.</p>
      <section className="mt-14" data-testid="genres-section">
        <h2 className="font-display text-2xl uppercase text-cyan-400">Genres We Book</h2>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[["Hip-Hop & R&B", "Headliners, legacy acts and regional breakout artists."], ["Country & Americana", "Arena country, Texas red dirt and songwriter rounds."],
            ["Gospel & Inspirational", "Choirs, worship nights and award-show caliber soloists."], ["Latin & Regional Mexican", "Banda, norteño, reggaetón and cumbia for Texas crowds."],
            ["EDM & DJs", "Festival mainstage DJs, afterparty residencies, open-format."], ["Zydeco & Southern Soul", "The trail-ride circuit — see roster below."],
            ["Comedy", "Theater and arena comedians, hosted showcases."], ["Speakers & Hosts", "Keynotes, celebrity hosts and MCs for corporate nights."]].map(([g, d]) => (
            <Link key={g} to={`/request?lane=talent`} data-testid={`genre-${g.split(" ")[0].toLowerCase().replace(/[^a-z]/g, "")}`} className="glass block p-5 transition-colors hover:border-white">
              <h3 className="font-cond text-lg uppercase tracking-wide text-white">{g}</h3>
              <p className="mt-2 text-xs text-neutral-400">{d}</p>
            </Link>
          ))}
        </div>
      </section>
      {GROUPS.map(([tier, label]) => {
        const list = artists.filter((a) => a.tier === tier);
        return list.length ? (
          <section key={tier} className="mt-14" data-testid={`artists-group-${tier}`}>
            <h2 className="font-display text-2xl uppercase text-cyan-400">{label}</h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{list.map((a, i) => <ArtistCard key={a.id} a={a} i={i} />)}</div>
          </section>
        ) : null;
      })}
      <div className="glass mt-16 flex flex-wrap items-center justify-between gap-4 rounded-3xl p-8">
        <div>
          <span className="eyebrow">Book these acts at your event</span>
          <p className="mt-1 text-neutral-300">EON produces the full lineup — talent, stage, sound and lights.</p>
        </div>
        <div className="flex gap-3">
          <Link to="/events" data-testid="artists-tickets-btn" className="btn-gold flex items-center gap-2 rounded-full px-6 py-3 font-cond font-bold uppercase"><Ticket className="h-4 w-4" /> See Lineups</Link>
          <Link to="/services" data-testid="artists-book-btn" className="btn-chrome rounded-full px-6 py-3 font-cond font-bold uppercase">Host With Us</Link>
        </div>
      </div>
    </div>
  );
}
